import { forwardRef, useId, useMemo } from "react";
import { cn } from "@/lib/cn";

export interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  showAge?: boolean;
  min?: string;
  max?: string;
  id?: string;
  name?: string;
  className?: string;
}

function calculateAge(dateStr: string): number | null {
  if (!dateStr) return null;
  const birth = new Date(dateStr);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
}

const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      onBlur,
      label,
      error,
      required,
      disabled,
      showAge = false,
      min,
      max,
      id: idProp,
      name,
      className,
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;

    const age = useMemo(() => (showAge && value ? calculateAge(value) : null), [showAge, value]);

    return (
      <div className={cn("space-y-1.5", className)}>
        {label && (
          <div className="flex items-center gap-2">
            <label
              htmlFor={id}
              className="text-sm font-medium leading-none text-foreground"
            >
              {label}
              {required && <span className="ml-0.5 text-destructive">*</span>}
            </label>
            {age !== null && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                Age: {age}
              </span>
            )}
          </div>
        )}
        <input
          ref={ref}
          type="date"
          id={id}
          name={name}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          min={min}
          max={max}
          disabled={disabled}
          required={required}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus-visible:ring-destructive",
          )}
        />
        {error && (
          <p id={`${id}-error`} className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  },
);
DatePicker.displayName = "DatePicker";

export { DatePicker, calculateAge };
